import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, ListItemIcon, Menu, MenuItem } from "@mui/material";
import { AddRounded, EventBusy } from "@mui/icons-material";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { SwayyPageHeader } from "../../components/SwayyPageShell";
import SwayyStatusTabs, {
  SWAYY_COUPON_STATUS_TAB_ICONS,
} from "../../components/SwayyStatusTabs";
import SwayySearchFiltersPanel, {
  SwayyFilterDateField,
  SwayyFilterDropdownField,
  SwayyFilterSearchField,
  SwayyFilterSearchInput,
  SWAYY_FILTER_DROPDOWN_DEFAULTS,
} from "../../components/filters/SwayySearchFiltersPanel";
import SwayyCouponsTable from "../../components/coupons/SwayyCouponsTable";
import SwayyCouponDetailDrawer from "../../components/coupons/SwayyCouponDetailDrawer";
import SwayyCreateCouponDialog from "../../components/coupons/SwayyCreateCouponDialog";
import ConfirmPopup from "../../../../../views/components/common/pop-up/ConfirmPopup";
import { SwayyCoupon } from "../../../../../models/SwayyCoupon/SwayyCoupon";
import {
  SWAYY_COUPON_CHANNEL_OPTIONS,
  SWAYY_COUPON_STATUS_TABS,
} from "../../../../../models/SwayyCoupon/SwayyCouponFilters";
import { getSwayyCouponAccess } from "../../../../../models/SwayyCoupon/SwayyCouponPermissions";
import { swayyCouponStore } from "../../../stores/SwayyCouponStore";
import { swayyColors, swayyRadii } from "../../../theme/tokens";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { userStore } from "../../../../../stores/sharedStores";

type SwayyCouponBulkActionResult = {
  succeeded: number;
  already: number;
  failed: number;
};

const notifySwayyCouponBulkResult = (
  result: SwayyCouponBulkActionResult,
  notify: {
    showError: (title: string, message: string) => void;
  },
  copy: {
    doneOne: string;
    doneMany: (count: number) => string;
    alreadyPart: (count: number) => string;
    failedAll: string;
  },
) => {
  const { succeeded, already, failed } = result;
  if (failed <= 0) return;

  if (succeeded === 0 && already === 0) {
    notify.showError("Error", copy.failedAll);
    return;
  }

  const parts: string[] = [];
  if (succeeded > 0) {
    parts.push(succeeded === 1 ? copy.doneOne : copy.doneMany(succeeded));
  }
  if (already > 0) {
    parts.push(copy.alreadyPart(already));
  }
  parts.push(
    failed === 1
      ? "1 coupon was not found."
      : `${failed} coupons were not found.`,
  );
  notify.showError("Error", parts.join(" "));
};

const SWAYY_COUPON_EXPIRE_COPY = {
  doneOne: "Coupon expired.",
  doneMany: (count: number) => `${count} coupons expired.`,
  alreadyPart: (count: number) =>
    count === 1 ? "1 was already expired." : `${count} were already expired.`,
  failedAll: "Failed to expire the selected coupons.",
};

const SwayyCoupons = () => {
  const store = swayyCouponStore;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError } = useNotification();
  const actionsButtonRef = useRef<HTMLDivElement | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { canView, canEdit, canCreate } = getSwayyCouponAccess(
    userStore.permissionMap,
  );

  const handleLoadCoupons = async () => {
    try {
      await store.getCoupons();
    } catch {
      showError("Error", "Failed to load coupons.");
    }
  };

  useEffect(() => {
    if (!canView) {
      return;
    }

    store.initializeParamsFromSearch(searchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  useEffect(() => {
    if (!store.urlReady || !canView) {
      return;
    }
    const nextParams = store.buildSearchParams();
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    store.urlReady,
    canView,
    store.params.page,
    store.params.limit,
    store.params.status,
    store.params.q,
    store.params.channel,
    store.params.starts_from,
    store.params.starts_to,
    store.params.ends_from,
    store.params.ends_to,
  ]);

  useEffect(() => {
    if (!store.urlReady || !canView) {
      return;
    }
    void handleLoadCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.urlReady, canView, store.params.page, store.params.limit, store.params.status]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      store.resetListPageUi();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilters = () => {
    store.applyFilters();
    void handleLoadCoupons();
  };

  const handleClearFilters = () => {
    store.clearFilters();
    void handleLoadCoupons();
  };

  const handleClearSearch = () => {
    store.setDraftQ("");
    handleApplyFilters();
  };

  const handleSearchChange = (value: string) => {
    store.setDraftQ(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      handleApplyFilters();
    }, 400);
  };

  const handleView = async (coupon: SwayyCoupon) => {
    try {
      await store.getCouponDetail(coupon.id);
    } catch {
      showError("Error", "Failed to load coupon details.");
    }
  };

  const handleExpireSelected = async () => {
    if (!canEdit) return;
    try {
      const result = await store.bulkExpireCoupons();
      notifySwayyCouponBulkResult(
        result,
        { showError },
        SWAYY_COUPON_EXPIRE_COPY,
      );
    } catch (error: any) {
      showError("Error", error?.message || "Failed to expire coupons.");
    }
  };

  if (!canView) {
    return <PermissionBlockLabel />;
  }

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        bgcolor: swayyColors.pageBg,
        minHeight: "100%",
      }}
    >
      <SwayyPageHeader
        title="Coupons"
        description={
          canEdit
            ? "Create and manage discount codes across channels"
            : "View discount codes across channels"
        }
        actions={
          canEdit ? (
            <>
              <Box ref={actionsButtonRef} sx={{ display: "inline-flex" }}>
                <AppButton
                  label="Actions"
                  onClick={() => {
                    try {
                      if (actionsButtonRef.current) {
                        store.openActionsMenu();
                      }
                    } catch {
                      showError("Error", "Failed to open actions.");
                    }
                  }}
                  customStyles={{
                    minWidth: 120,
                    height: 40,
                    minHeight: 40,
                    borderRadius: swayyRadii.md,
                    bgcolor: swayyColors.pine,
                    color: swayyColors.white,
                    "&:hover": { bgcolor: swayyColors.pineHover },
                  }}
                />
              </Box>
              <Menu
                anchorEl={store.actionsMenuOpen ? actionsButtonRef.current : null}
                open={store.actionsMenuOpen}
                onClose={store.closeActionsMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      minWidth: 240,
                      mt: 0.75,
                      borderRadius: swayyRadii.md,
                      border: `1px solid ${swayyColors.border}`,
                    },
                  },
                }}
              >
                {canCreate ? (
                  <MenuItem
                    onClick={() => {
                      store.closeActionsMenu();
                      store.openCreate();
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: swayyColors.textSecondary,
                        "& .MuiSvgIcon-root": { fontSize: 18 },
                      }}
                    >
                      <AddRounded />
                    </ListItemIcon>
                    <CustomTypography variant="body2">
                      Add coupon
                    </CustomTypography>
                  </MenuItem>
                ) : null}
                <MenuItem
                  disabled={store.saving}
                  onClick={() => {
                    store.closeActionsMenu();
                    if (store.selectedCount === 0) {
                      showError("Error", "Select at least one coupon.");
                      return;
                    }
                    store.openExpireConfirm();
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: swayyColors.textSecondary,
                      "& .MuiSvgIcon-root": { fontSize: 18 },
                    }}
                  >
                    <EventBusy />
                  </ListItemIcon>
                  <CustomTypography variant="body2">
                    {store.selectedCount > 0
                      ? `Expire (${store.selectedCount})`
                      : "Expire"}
                  </CustomTypography>
                </MenuItem>
              </Menu>
            </>
          ) : undefined
        }
      />

      <SwayySearchFiltersPanel
        open={store.filtersOpen}
        onToggle={store.toggleFiltersOpen}
        onClear={handleClearFilters}
        onApply={handleApplyFilters}
      >
        <SwayyFilterSearchField>
          <SwayyFilterSearchInput
            value={store.draftQ}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
            placeholder="Search coupon code..."
          />
        </SwayyFilterSearchField>
        <SwayyFilterDropdownField>
          <AppSearchDropdownMulti
            title="Select Channel"
            isMultiSelect={false}
            list={SWAYY_COUPON_CHANNEL_OPTIONS}
            value={SWAYY_COUPON_CHANNEL_OPTIONS.filter((option) =>
              store.draftChannel.includes(option.id),
            )}
            {...SWAYY_FILTER_DROPDOWN_DEFAULTS}
            handleSelect={(items) =>
              store.setDraftChannel(items.map((item) => item.id))
            }
          />
        </SwayyFilterDropdownField>
        <SwayyFilterDateField>
          <DateRangePickerNew
            placeholder="Start date from"
            disableFutureDates={false}
            startDate={
              store.draftStartsFrom ? dayjs(store.draftStartsFrom) : null
            }
            endDate={store.draftStartsTo ? dayjs(store.draftStartsTo) : null}
            onRangeSelect={(range) => {
              store.setDraftStartDateRange(
                range.startDate ? range.startDate.format("YYYY-MM-DD") : "",
                range.endDate ? range.endDate.format("YYYY-MM-DD") : "",
              );
            }}
          />
        </SwayyFilterDateField>
        <SwayyFilterDateField>
          <DateRangePickerNew
            placeholder="Start date to"
            disableFutureDates={false}
            startDate={
              store.draftEndsFrom ? dayjs(store.draftEndsFrom) : null
            }
            endDate={store.draftEndsTo ? dayjs(store.draftEndsTo) : null}
            onRangeSelect={(range) => {
              store.setDraftEndDateRange(
                range.startDate ? range.startDate.format("YYYY-MM-DD") : "",
                range.endDate ? range.endDate.format("YYYY-MM-DD") : "",
              );
            }}
          />
        </SwayyFilterDateField>
      </SwayySearchFiltersPanel>

      <SwayyStatusTabs
        value={store.statusTab}
        onChange={store.setStatusTab}
        tabs={SWAYY_COUPON_STATUS_TABS}
        icons={SWAYY_COUPON_STATUS_TAB_ICONS}
      />

      <SwayyCouponsTable
        selectable={canEdit}
        canEdit={canEdit}
        onView={handleView}
        onEdit={(coupon: SwayyCoupon) => {
          if (!canEdit) return;
          store.openEdit(coupon);
        }}
        onRetry={handleLoadCoupons}
      />
      <SwayyCouponDetailDrawer
        onOpenOrder={(orderId) => navigate(`/orders/${orderId}`)}
      />
      {canEdit ? (
        <SwayyCreateCouponDialog
          canCreate={canCreate}
          canEdit={canEdit}
          onError={(message) => showError("Error", message)}
        />
      ) : null}

      <ConfirmPopup
        mode="swayy"
        open={canEdit && store.expireOpen}
        title="Expire coupons?"
        message={
          store.selectedCount === 1
            ? "The selected coupon will be marked expired and can no longer be used at checkout."
            : `${store.selectedCount} selected coupons will be marked expired and can no longer be used at checkout.`
        }
        cancelText="Cancel"
        confirmText={store.saving ? "Expiring..." : "Expire"}
        loading={store.saving}
        onCancel={store.closeExpireConfirm}
        onConfirm={() => {
          void handleExpireSelected();
        }}
      />
    </Box>
  );
};

export default observer(SwayyCoupons);
